import { useState, useEffect, useCallback, useRef } from 'react';
import { VERSION_CHECK_INTERVAL } from '@/utils/constants';

interface VersionCheckResult {
  /** Whether a new version is available */
  hasUpdate: boolean;
  /** The latest version string from the server */
  latestVersion: string | null;
  /** Dismiss the update notification */
  dismiss: () => void;
}

/**
 * Version polling hook.
 *
 * Fetches `/version.json` every 5 minutes to detect new deployments.
 * Pauses polling when the page is hidden (Page Visibility API) and
 * resumes + checks immediately when the page becomes visible again.
 */
export function useVersionCheck(): VersionCheckResult {
  const [hasUpdate, setHasUpdate] = useState(false);
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  const initialVersionRef = useRef<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const checkVersion = useCallback(async () => {
    try {
      const resp = await fetch(`/version.json?t=${Date.now()}`);
      if (!resp.ok) return;
      const data = (await resp.json()) as { version: string };

      if (initialVersionRef.current === null) {
        initialVersionRef.current = data.version;
      } else if (data.version !== initialVersionRef.current) {
        setHasUpdate(true);
        setLatestVersion(data.version);
      }
    } catch {
      // Silently ignore fetch errors
    }
  }, []);

  const startPolling = useCallback(() => {
    if (intervalRef.current) return;
    intervalRef.current = setInterval(checkVersion, VERSION_CHECK_INTERVAL);
  }, [checkVersion]);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- legitimate: initial async fetch on mount
    void checkVersion();
    startPolling();

    function handleVisibilityChange() {
      if (document.hidden) {
        stopPolling();
      } else {
        void checkVersion();
        startPolling();
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkVersion, startPolling, stopPolling]);

  const dismiss = useCallback(() => {
    setHasUpdate(false);
    // Update the baseline so we don't re-alert for the same version
    if (latestVersion) {
      initialVersionRef.current = latestVersion;
    }
  }, [latestVersion]);

  return { hasUpdate, latestVersion, dismiss };
}
